import type { LucideIcon } from "lucide-react";
import {
  Archive,
  LayoutGrid,
  Users,
  ListChecks,
  UserCog,
  ScrollText,
  Settings,
  Package,
  Landmark,
  FileText,
  Target,
  FolderKanban,
  Megaphone,
  FileBarChart,
  LayoutDashboard,
  ImageIcon,
  Library,
  CheckSquare,
  Bot,
  MessageSquare,
  Handshake,
  UserPlus,
  RefreshCw,
  CreditCard,
} from "lucide-react";

export type NavGroup =
  | "operations"
  | "marketing"
  | "compta"
  | "reports"
  | "ai"
  | "team"
  /** Spec §6 — « Paramètres » est une section autonome, plus une sous-entrée d'Équipe. */
  | "settings";

export interface NavItem {
  /** route path under "/" */
  to: string;
  /** i18n key for the label */
  labelKey: string;
  /** fallback label (FR) */
  label: string;
  icon: LucideIcon;
  group: NavGroup;
  /** optional permission key required to see this item */
  permission?: string;
  /** when true, only OWNER/ADMIN/MANAGER can see this item */
  adminOnly?: boolean;
  /** when set, user must have one of these roles (overrides permission-based access) */
  restrictRoles?: string[];
  badge?: { value: number; tone: "primary" | "danger" };
}

export const NAV_ITEMS: NavItem[] = [
  {
    to: "/dashboard",
    labelKey: "nav.dashboard",
    label: "Tableau de bord",
    icon: LayoutGrid,
    group: "operations",
    adminOnly: true,
  },
  {
    to: "/leads",
    labelKey: "nav.leads",
    label: "Prospects (CRM)",
    icon: Target,
    group: "operations",
    permission: "leads.view",
  },
  {
    to: "/clients",
    labelKey: "nav.clients",
    label: "Clients",
    icon: Users,
    group: "operations",
    permission: "clients.view",
  },
  {
    to: "/services",
    labelKey: "nav.services",
    label: "Catalogue de services",
    icon: Package,
    group: "operations",
    permission: "services.view",
  },
  {
    to: "/projects",
    labelKey: "nav.projects",
    label: "Projets",
    icon: FolderKanban,
    group: "operations",
    permission: "projects.view",
  },
  {
    to: "/tasks",
    labelKey: "nav.tasks",
    label: "Tâches",
    icon: ListChecks,
    group: "operations",
    permission: "tasks.view",
  },
  // ── Marketing Hub ──
  {
    to: "/marketing",
    labelKey: "nav.marketing",
    label: "Vue d'ensemble",
    icon: LayoutDashboard,
    group: "marketing",
    permission: "marketing.view",
  },
  {
    to: "/marketing/campagnes",
    labelKey: "nav.marketingCampaigns",
    label: "Campagnes",
    icon: Megaphone,
    group: "marketing",
    permission: "marketing.view",
  },
  {
    to: "/marketing/contenu",
    labelKey: "nav.marketingContent",
    label: "Créatifs & Contenu",
    icon: ImageIcon,
    group: "marketing",
    permission: "content.view",
  },
  {
    to: "/marketing/bibliotheque",
    labelKey: "nav.marketingLibrary",
    label: "Bibliothèque",
    icon: Library,
    group: "marketing",
    permission: "marketing.view",
  },
  {
    to: "/marketing/approbations",
    labelKey: "nav.marketingApprovals",
    label: "Approbations",
    icon: CheckSquare,
    group: "marketing",
    permission: "approvals.view",
  },
  // ── Partenaires & Affiliés (spec-partenaires-affilies.txt §1) ──
  {
    to: "/marketing/partenaires",
    labelKey: "nav.marketingPartners",
    label: "Partenaires",
    icon: Handshake,
    group: "marketing",
    permission: "partners.view",
  },
  {
    to: "/marketing/affilies",
    labelKey: "nav.marketingAffiliates",
    label: "Affiliés",
    icon: UserPlus,
    group: "marketing",
    permission: "affiliates.view",
  },
  // ── Finance (ex-Comptabilité, renommée — spec modifications §7) ──
  {
    to: "/compta",
    labelKey: "nav.comptaOverview",
    label: "Vue d'ensemble",
    icon: LayoutGrid,
    group: "compta",
    permission: "accounting.view",
    restrictRoles: ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"],
  },
  {
    to: "/invoices",
    labelKey: "nav.invoices",
    label: "Facturation",
    icon: FileText,
    group: "compta",
    permission: "invoices.view",
    restrictRoles: ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"],
  },
  {
    to: "/treasury",
    labelKey: "nav.treasury",
    label: "Trésorerie",
    icon: Landmark,
    group: "compta",
    permission: "treasury.view",
    restrictRoles: ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"],
  },
  {
    to: "/compta/dettes",
    labelKey: "nav.comptaDebts",
    label: "Dettes & Créances",
    icon: Landmark,
    group: "compta",
    permission: "accounting.view",
    restrictRoles: ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"],
  },
  {
    // Les récurrentes n'étaient qu'un onglet d'Entrées & Sorties : elles ont
    // leur propre page pour piloter l'engagement mensuel de l'agence.
    to: "/compta/recurrentes",
    labelKey: "nav.comptaRecurring",
    label: "Dépenses récurrentes",
    icon: RefreshCw,
    group: "compta",
    permission: "accounting.view",
    restrictRoles: ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"],
  },
  {
    to: "/subscriptions",
    labelKey: "nav.subscriptions",
    label: "Abonnements",
    icon: CreditCard,
    group: "compta",
    permission: "subscriptions.view",
    restrictRoles: ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"],
  },
  {
    // Spec « dernières modifications » §7 — une SEULE page Rapports
    // (consultation + génération en modal).
    to: "/rapports",
    labelKey: "nav.reportsSaved",
    label: "Rapports",
    icon: FileBarChart,
    group: "reports",
    permission: "reports.view",
  },
  {
    to: "/reports",
    labelKey: "nav.reports",
    label: "Rapports détaillés",
    icon: FileText,
    group: "reports",
    permission: "reports.view",
    restrictRoles: ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"],
  },
  // ── Assistants IA (spec docs/spec-ai-agents.md §1) ──
  {
    to: "/agents-ia",
    labelKey: "nav.aiAgents",
    label: "Assistants IA",
    icon: Bot,
    group: "ai",
    permission: "ai-agents.view",
  },
  {
    to: "/agents-ia/chat",
    labelKey: "nav.aiChat",
    label: "Agents & Chat",
    icon: MessageSquare,
    group: "ai",
    permission: "ai-agents.view",
  },
  {
    to: "/team/dashboard",
    labelKey: "nav.teamDashboard",
    label: "Performance",
    icon: LayoutGrid,
    group: "team",
    permission: "team.view",
  },
  {
    to: "/team",
    labelKey: "nav.team",
    label: "Équipe",
    icon: UserCog,
    group: "team",
    permission: "team.view",
  },
  // Spec Équipe — « Suppression de Rôles & Organisation » du menu : la gestion
  // passe par Paramètres > Rôles & permissions. La ROUTE /team/organisation
  // reste servie (les liens existants ne doivent pas casser) et sa règle
  // d'accès est déclarée dans route-permissions.ts, faute de NAV_ITEMS.
  {
    // Spec « dernières modifications » §4 — traçabilité globale.
    to: "/journal",
    labelKey: "nav.auditLogs",
    label: "Journal des opérations",
    icon: ScrollText,
    group: "team",
    permission: "audit-logs.view",
  },
  {
    // Corbeille visible de toute l'app : chaque « Supprimer » archive ici ;
    // restauration + suppression définitive. Zone d'administration — le
    // backend re-vérifie OWNER/ADMIN sur droits vivants.
    to: "/archives",
    labelKey: "nav.archives",
    label: "Archives",
    icon: Archive,
    group: "team",
    restrictRoles: ["OWNER", "ADMIN"],
  },
  // ── Paramètres (spec §6 — section indépendante, toujours en dernier) ──
  {
    to: "/settings",
    labelKey: "nav.settings",
    label: "Paramètres",
    icon: Settings,
    group: "settings",
    permission: "settings.view",
  },
];

export const NAV_GROUP_LABELS: Record<NavGroup, { key: string; label: string }> = {
  operations: { key: "nav.group.operations", label: "Opérations" },
  marketing: { key: "nav.group.marketing", label: "Marketing" },
  compta: { key: "nav.group.compta", label: "Finance" },
  reports: { key: "nav.group.reports", label: "Rapports" },
  ai: { key: "nav.group.ai", label: "Assistants IA" },
  team: { key: "nav.group.team", label: "Équipe" },
  // Section à entrée unique : réutilise la clé de l'entrée (pas de libellé de
  // groupe redondant « Paramètres > Paramètres »).
  settings: { key: "nav.settings", label: "Paramètres" },
};

export const DEFAULT_EXCHANGE_RATE = 218.5;
export const WALLET_ALERT_THRESHOLD_USD = 150;

export const AGENCY = {
  name: "Fad Agency",
  slug: "fad-agency",
  email: "contact@fadagency.dz",
};
